import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, TOKEN_KEY } from "@/lib/api";
import { del } from "idb-keyval";

const USER_KEY = "rollsmartr_user";
const cachedUser = () => { try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; } };
import { isIOS, isStandalone } from "@/lib/pwa";

const handoffCode = new URLSearchParams(window.location.search).get("handoff");

let redeemP = null;

function redeemHandoff() {
  redeemP ||= (async () => {
    window.history.replaceState(null, "", window.location.pathname);
    const { data } = await api.post("/auth/handoff/redeem", { code: handoffCode });
    localStorage.setItem(TOKEN_KEY, data.token);
    return data.user;
  })();
  return redeemP;
}

// Installed home-screen apps can have empty localStorage; restore from the httpOnly session cookie if it was shared.
async function restoreFromCookie() {
  const { data } = await api.post("/auth/session");
  localStorage.setItem(TOKEN_KEY, data.token);
  return data.user;
}

function useInstallHandoff(user) {
  useEffect(() => {
    if (!user || !isIOS() || isStandalone()) return;
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const refresh = () => api.post("/auth/handoff").then(({ data }) => {
      link.href = `${process.env.REACT_APP_BACKEND_URL}/api/pwa/manifest?code=${data.code}`;
    }).catch(() => {});
    refresh();
    const t = setInterval(refresh, 20 * 60 * 1000);
    return () => clearInterval(t);
  }, [user]);
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(() => (localStorage.getItem(TOKEN_KEY) ? cachedUser() || null : null));
  const setUser = useCallback((v) => setUserState((prev) => {
    const next = typeof v === "function" ? v(prev) : v;
    if (next) localStorage.setItem(USER_KEY, JSON.stringify(next));
    else localStorage.removeItem(USER_KEY);
    return next;
  }), []);
  const qc = useQueryClient();
  useInstallHandoff(user ? user.id : null);

  useEffect(() => {
    if (handoffCode && (redeemP || !localStorage.getItem(TOKEN_KEY))) {
      redeemHandoff().catch(restoreFromCookie).then(setUser).catch(() => setUser(false));
      return;
    }
    if (handoffCode) window.history.replaceState(null, "", window.location.pathname);
    if (!localStorage.getItem(TOKEN_KEY)) {
      restoreFromCookie().then(setUser).catch(() => setUser(false));
      return;
    }
    api.get("/auth/me").then((r) => setUser(r.data)).catch((err) => {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      } else if (!cachedUser()) {
        setUser((u) => u || false);
      }
    });
  }, [setUser]);

  const signIn = useCallback(async (mode, email, password, extra = {}) => {
    const { data } = await api.post(`/auth/${mode}`, { email, password, ...extra });
    localStorage.setItem(TOKEN_KEY, data.token);
    qc.clear();
    setUser(data.user);
    return data.user;
  }, [qc, setUser]);

  const logout = useCallback(() => {
    api.post("/auth/logout", null, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } }).catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    qc.clear();
    del("rollsmartr-cache").catch(() => {});
    setUser(false);
  }, [qc, setUser]);

  return <AuthContext.Provider value={{ user, setUser, signIn, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
